// src/pages/Supplier.js

import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    InputAdornment,
    Menu,
    Tooltip,
    Card,
    CardContent,
    CardActions,
    Tabs,
    Tab,
    Avatar,
    Alert,
    Snackbar,
    ToggleButton,
    ToggleButtonGroup,
    ListItemIcon,
    ListItemText
} from '@mui/material';

import {
    Delete,
    Search,
    Add as AddIcon,
    Close as CloseIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Today as TodayIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    PictureAsPdf,
    TableChart,
    GetApp,
    Print,
    Person,
    Phone,
    Email,
    LocationOn,
    Business,
    CalendarMonth,
    Badge,
    ViewModule,
    ViewList,
    Inventory,
    LocalShipping,
    Warehouse,
    MoreVert
} from '@mui/icons-material';

import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APISupplierList,
    APISupplierStore,
    APISupplierUpdate,
    APISupplierDelete,
    APISupplierExportExcel,
    APISupplierExportPDF,
    APISupplierPrint,
    APISupplierStats
} from '../../apiServices/APIs';
import { formatCreatedAtDate, recordsCount, recordsPerPage } from '../../utils/helpers';
import { getUserDetails } from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import { usePermission } from '../../hooks/usePermission';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorrState';
import SystemInformation from '../../components/SystemInformation';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const Supplier = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
    const [selectedSupplierForAction, setSelectedSupplierForAction] = useState(null);
    const [pageState, setPageState] = useState({
        pageData: [],
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState(0);
    const [viewMode, setViewMode] = useState('list');
    const [statistics, setStatistics] = useState(null);

    // Form data for Supplier
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        location: ''
    });

    const [filters, setFilters] = useState({
        name: '',
        phone: '',
        email: '',
        location: '',
        dateFrom: '',
        dateTo: ''
    });

    const [errors, setErrors] = useState({
        name: false,
        phone: false,
        email: false,
        location: false
    });

    // State for dialogs
    const [filterOptions, setFilterOptions] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    // Use the updated permission hook
    const { hasPermission, loading: permissionLoading } = usePermission();

    useEffect(() => {
        apiService.setToastFunction(showToast);
        fetchData();
        fetchStatistics();
    }, [showToast]);

    // Fetch suppliers
    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APISupplierList);

            if (result.success) {
                const suppliers = Array.isArray(result.data) ? result.data : [];
                setPageState(prev => ({
                    ...prev,
                    pageData: suppliers
                }));
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError("Failed to load data");
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch statistics
    const fetchStatistics = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.listStats(userBranchId, APISupplierStats);
            if (result.success && result.data) {
                setStatistics(result.data);
            }
        } catch (error) {
            console.error("Error fetching statistics:", error);
        }
    };

    // Export handlers
    const handleExportClick = (event) => { setExportAnchorEl(event.currentTarget); };
    const handleExportClose = () => { setExportAnchorEl(null); };

    const handlePrint = async (record) => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to print supplier details", "error");
            return;
        }

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('print_id', record.id);
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.print(APISupplierPrint, formDataToSend);

            if (result && result.success) {
                showToast("Success", "PDF file generated successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to generate PDF file", "error");
            }
        } catch (error) {
            console.error("Print error:", error);
            showToast("Error", "Failed to generate PDF file", "error");
        }
    };

    const handleExportExcel = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export supplier data", "error");
            handleExportClose();
            return;
        }

        try {
            handleExportClose();
            showToast("Processing", "Preparing Excel export...", "info");
            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            
            // Append filters if any
            if (filters.name) formDataToSend.append('name', filters.name);
            if (filters.phone) formDataToSend.append('phone', filters.phone);
            if (filters.email) formDataToSend.append('email', filters.email);
            if (filters.location) formDataToSend.append('location', filters.location);
            if (filters.dateFrom) formDataToSend.append('date_from', filters.dateFrom);
            if (filters.dateTo) formDataToSend.append('date_to', filters.dateTo);
            
            const result = await apiService.exportToExcel(APISupplierExportExcel, formDataToSend);
            if (result && result.success) {
                showToast("Success", "Excel file downloaded successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to export Excel file", "error");
            }
        } catch (error) {
            console.error('Excel export error:', error);
            showToast("Error", error.message || "Failed to export Excel file", "error");
        }
    };

    const handleExportPDF = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export supplier data", "error");
            handleExportClose();
            return;
        }

        try {
            handleExportClose();
            showToast("Processing", "Preparing PDF export...", "info");

            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            
            // Append filters if any
            if (filters.name) formDataToSend.append('name', filters.name);
            if (filters.location) formDataToSend.append('location', filters.location);
            
            const result = await apiService.exportToPDF(APISupplierExportPDF, formDataToSend);

            if (result && result.success) {
                showToast("Success", "PDF file downloaded successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to export PDF file", "error");
            }
        } catch (error) {
            console.error('PDF export error:', error);
            showToast("Error", error.message || "Failed to export PDF file", "error");
        }
    };

    // Filter based on search term and filters
    const filteredData = pageState.pageData.filter(supplier => {
        const matchesSearch = searchTerm === '' ||
            supplier.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            supplier.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            supplier.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            supplier.location?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesName = !filters.name ||
            supplier.name?.toLowerCase().includes(filters.name.toLowerCase());

        const matchesPhone = !filters.phone ||
            supplier.phone?.toLowerCase().includes(filters.phone.toLowerCase());

        const matchesEmail = !filters.email ||
            supplier.email?.toLowerCase().includes(filters.email.toLowerCase());

        const matchesLocation = !filters.location ||
            supplier.location?.toLowerCase().includes(filters.location.toLowerCase());

        let matchesDate = true;
        if (filters.dateFrom) {
            const supplierDate = new Date(supplier.created_at).toISOString().split('T')[0];
            const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0];
            matchesDate = matchesDate && supplierDate >= filterFromDate;
        }

        if (filters.dateTo) {
            const supplierDate = new Date(supplier.created_at).toISOString().split('T')[0];
            const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0];
            matchesDate = matchesDate && supplierDate <= filterToDate;
        }

        return matchesSearch && matchesName && matchesPhone && matchesEmail && matchesLocation && matchesDate;
    });

    // Calculate totals from statistics
    const calculateTotals = () => {
        if (statistics) {
            return {
                total: statistics.total_suppliers || 0,
                thisMonth: statistics.this_month_suppliers || 0,
                lastMonth: statistics.last_month_suppliers || 0,
                byLocation: statistics.suppliers_by_location || {}
            };
        }
        
        return {
            total: filteredData.length,
            thisMonth: 0,
            lastMonth: 0,
            byLocation: {}
        };
    };

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Dialog handlers
    const handleFilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);

    const handleOpenAdd = () => {
        if (!hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add suppliers", "error");
            return;
        }

        setPostStatus("ADD");
        setFormData({
            name: '',
            phone: '',
            email: '',
            location: ''
        });
        setErrors({
            name: false,
            phone: false,
            email: false,
            location: false
        });
        setOpenAdd(true);
    };

    const handleCloseAdd = () => {
        setOpenAdd(false);
    };

    const handleOpenView = (record) => {
        setSelectedRecord(record);
        setOpenView(true);
        handleActionMenuClose();
    };

    const handleCloseView = () => {
        setOpenView(false);
        setActiveTab(0);
    };

    const handleOpenEdit = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to edit suppliers", "error");
            return;
        }

        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("UPDATE");

        setFormData({
            name: record.name || '',
            phone: record.phone || '',
            email: record.email || '',
            location: record.location || ''
        });
        setOpenAdd(true);
        handleActionMenuClose();
    };

    const handleOpenDelete = (record) => {
        if (!hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete suppliers", "error");
            return;
        }

        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("DELETE");
        setOpenDelete(true);
        handleActionMenuClose();
    };

    const handleCloseDelete = () => {
        setOpenDelete(false);
    };

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setErrors(prev => ({
            ...prev,
            [name]: false
        }));

        if (name === 'phone') {
            if (value === '' || /^[\d\s\-()+]*$/.test(value)) {
                setFormData(prev => ({
                    ...prev,
                    [name]: value
                }));
            } else {
                setErrors(prev => ({
                    ...prev,
                    [name]: true
                }));
                showToast("Input Error", "Please enter valid phone characters only", "error");
                return;
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Filter handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const applyTodayFilter = () => {
        const today = new Date().toISOString().split('T')[0];
        setFilters({
            ...filters,
            dateFrom: today,
            dateTo: today
        });
        handleCloseOptions();
    };

    const applyThisMonthFilter = () => {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];

        setFilters({
            ...filters,
            dateFrom: firstDay,
            dateTo: lastDay
        });
        handleCloseOptions();
    };

    const clearFilters = () => {
        setFilters({
            name: '',
            phone: '',
            email: '',
            location: '',
            dateFrom: '',
            dateTo: ''
        });
        setSearchTerm('');
        handleCloseOptions();
    };

    const applyFilters = () => {
        handleCloseOptions();
    };

    // Action menu handlers
    const handleActionMenuOpen = (event, supplier) => {
        event.stopPropagation();
        setActionMenuAnchorEl(event.currentTarget);
        setSelectedSupplierForAction(supplier);
    };

    const handleActionMenuClose = () => {
        setActionMenuAnchorEl(null);
        setSelectedSupplierForAction(null);
    };

    const validateForm = () => {
        if (!formData.name?.trim()) {
            showToast("Validation Error", "Supplier name is required", "warning");
            return false;
        }
        if (!formData.phone?.trim()) {
            showToast("Validation Error", "Phone number is required", "warning");
            return false;
        }
        if (!formData.email?.trim()) {
            showToast("Validation Error", "Email address is required", "warning");
            return false;
        }
        if (!formData.location?.trim()) {
            showToast("Validation Error", "Location is required", "warning");
            return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (formData.email && !emailRegex.test(formData.email)) {
            showToast("Validation Error", "Please enter a valid email address", "warning");
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (postStatus === "ADD" && !hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add suppliers", "error");
            return;
        }

        if (postStatus === "UPDATE" && !hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to update suppliers", "error");
            return;
        }

        if (postStatus === "DELETE" && !hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete suppliers", "error");
            return;
        }

        if (postStatus !== "DELETE") {
            if (!validateForm()) return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));

        if (postStatus !== "DELETE") {
            formDataToSend.append('name', formData.name);
            formDataToSend.append('phone', formData.phone);
            formDataToSend.append('email', formData.email);
            formDataToSend.append('location', formData.location);
        }

        if (postStatus === "UPDATE" || postStatus === "DELETE") {
            formDataToSend.append('updateId', updateId);
        }

        try {
            let result;
            if (postStatus === "ADD") {
                result = await apiService.create(APISupplierStore, formDataToSend);
            } else if (postStatus === "UPDATE") {
                result = await apiService.update(APISupplierUpdate, formDataToSend);
            } else if (postStatus === "DELETE") {
                result = await apiService.delete(APISupplierDelete, formDataToSend);
            }

            if (result && result.success) {
                showToast("Success", result.message, "success");
                handleCloseAdd();
                handleCloseDelete();
                fetchData();
                fetchStatistics();
            }
        } catch (error) {
            console.error("API Error:", error);
        }
    };

    // Render grid view
    const renderGridView = () => {
        if (viewMode !== 'grid') return null;

        const currentData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

        if (currentData.length === 0) {
            return (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Business sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        No suppliers found
                    </Typography>
                </Box>
            );
        }

        return (
            <Grid container spacing={2} sx={{ p: 2 }}>
                {currentData.map((record) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={record.id}>
                        <Card sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            '&:hover': { boxShadow: 6 }
                        }}>
                            <CardContent sx={{ flexGrow: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                    <Avatar sx={{ 
                                        bgcolor: theme.palette.primary.main,
                                        width: 56,
                                        height: 56
                                    }}>
                                        <Business sx={{ fontSize: 32 }} />
                                    </Avatar>
                                    <IconButton 
                                        size="small" 
                                        sx={{ color: theme.palette.action.main }}
                                        onClick={(e) => handleActionMenuOpen(e, record)}
                                        aria-label="actions"
                                    >
                                        <MoreVert />
                                    </IconButton>
                                </Box>
                                <Typography variant="h6" component="div" gutterBottom>
                                    {record.name}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                    <Chip
                                        icon={<Phone />}
                                        label={record.phone}
                                        color="primary"
                                        variant="outlined"
                                        size="small"
                                    />
                                    <Chip
                                        icon={<Email />}
                                        label={record.email}
                                        color="success"
                                        variant="outlined"
                                        size="small"
                                    />
                                </Box>
                                <Box sx={{ display: 'flex' }}>
                                    <Chip
                                        icon={<LocationOn />}
                                        label={record.location}
                                        color="warning"
                                        variant="outlined"
                                        size="small"
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        );
    };

    const totals = calculateTotals();

    if (permissionLoading) {
        return <LoadingState message="Loading permissions..." variant="medical" size="large" fullPage />;
    }

    if (loading) {
        return (
            <LoadingState
                message="Loading Supplier Data..."
                variant="medical"
                size="large"
                fullPage
            />
        );
    }

    if (error) {
        return (
            <ErrorState
                error={error}
                onRetry={fetchData}
                title="Unable to Load Supplier Data"
                variant="medical"
                fullPage
                retryText="Retry Loading"
            />
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Statistics Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={4}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Total Suppliers</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                    {totals.total}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.primary.light, width: 56, height: 56 }}>
                                <Business sx={{ fontSize: 32 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">This Month</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                    {totals.thisMonth}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.success.light, width: 56, height: 56 }}>
                                <CalendarMonth sx={{ fontSize: 32 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Last Month</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.info.main }}>
                                    {totals.lastMonth}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.info.light, width: 56, height: 56 }}>
                                <CalendarMonth sx={{ fontSize: 32 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Search and Filter Bar */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{
                        fontWeight: 'bold',
                        color: theme.palette.primary.main,
                        flexShrink: 0
                    }}>
                        Supplier Management ({recordsCount(pageState.pageData.length)} Total)
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        {hasPermission('ADD') && (
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<AddIcon />}
                                onClick={handleOpenAdd}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                                size="small"
                            >
                                Add Supplier
                            </Button>
                        )}

                        {hasPermission('VIEW') && (
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<FilterIcon />}
                                onClick={handleFilterOptions}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                                size="small"
                            >
                                View Options
                            </Button>
                        )}

                        {hasPermission('EXPORT') && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExportClick}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                                size="small"
                            >
                                Export Data
                            </Button>
                        )}

                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<RefreshIcon />}
                            onClick={() => {
                                fetchData();
                                fetchStatistics();
                            }}
                            sx={{ textTransform: 'none', borderRadius: '8px' }}
                            size="small"
                        >
                            Refresh
                        </Button>

                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(e, val) => val && setViewMode(val)}
                            size="small"
                        >
                            <ToggleButton value="list">
                                <ViewList />
                            </ToggleButton>
                            <ToggleButton value="grid">
                                <ViewModule />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <Paper sx={{
                        width: '300px',
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: 'auto',
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search suppliers..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': {
                                        color: 'white',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            opacity: 1
                                        }
                                    }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>

                {/* Active Filters */}
                {(filters.name || filters.phone || filters.email || filters.location || filters.dateFrom || filters.dateTo) && (
                    <Box sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            Active filters:
                        </Typography>
                        {filters.name && (
                            <Chip
                                label={`Name: ${filters.name}`}
                                onDelete={() => setFilters(prev => ({ ...prev, name: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.phone && (
                            <Chip
                                label={`Phone: ${filters.phone}`}
                                onDelete={() => setFilters(prev => ({ ...prev, phone: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.email && (
                            <Chip
                                label={`Email: ${filters.email}`}
                                onDelete={() => setFilters(prev => ({ ...prev, email: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.location && (
                            <Chip
                                label={`Location: ${filters.location}`}
                                onDelete={() => setFilters(prev => ({ ...prev, location: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.dateFrom && (
                            <Chip
                                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.dateTo && (
                            <Chip
                                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        <Button
                            size="small"
                            onClick={clearFilters}
                            startIcon={<CloseIcon fontSize="small" />}
                            sx={{ ml: 1 }}
                        >
                            Clear All
                        </Button>
                    </Box>
                )}
            </Paper>

            {/* Export Menu */}
            <Menu
                anchorEl={exportAnchorEl}
                open={Boolean(exportAnchorEl)}
                onClose={handleExportClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
                    <TableChart fontSize="small" color="success" />
                    Export to Excel (.xlsx)
                </MenuItem>
                <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
                    <PictureAsPdf fontSize="small" color="error" />
                    Export to PDF (.pdf)
                </MenuItem>
            </Menu>

            {/* Action Menu for List Items */}
            <Menu
                anchorEl={actionMenuAnchorEl}
                open={Boolean(actionMenuAnchorEl)}
                onClose={handleActionMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {hasPermission('VIEW') && (
                    <MenuItem onClick={() => selectedSupplierForAction && handleOpenView(selectedSupplierForAction)}>
                        <ListItemIcon><ViewIcon fontSize="small" color="primary" /></ListItemIcon>
                        <ListItemText sx={{ color: 'primary.main' }}>View</ListItemText>
                    </MenuItem>
                )}
                {hasPermission('PRINT') && (
                    <MenuItem onClick={() => selectedSupplierForAction && handlePrint(selectedSupplierForAction)}>
                        <ListItemIcon><Print fontSize="small" color="warning" /></ListItemIcon>
                        <ListItemText sx={{ color: 'warning.main' }}>Print</ListItemText>
                    </MenuItem>
                )}
                {hasPermission('EDIT') && (
                    <MenuItem onClick={() => selectedSupplierForAction && handleOpenEdit(selectedSupplierForAction)}>
                        <ListItemIcon><EditIcon fontSize="small" color="secondary" /></ListItemIcon>
                        <ListItemText sx={{ color: 'secondary.main' }}>Edit</ListItemText>
                    </MenuItem>
                )}
                {hasPermission('DELETE') && (
                    <MenuItem onClick={() => selectedSupplierForAction && handleOpenDelete(selectedSupplierForAction)}>
                        <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
                        <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
                    </MenuItem>
                )}
            </Menu>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                {/* List View */}
                {viewMode === 'list' && (
                    <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{
                                        backgroundColor: theme.palette.primary.main,
                                        '& th': {
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: '0.95rem'
                                        }
                                    }}>
                                        <TableCell>#</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Phone</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Location</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((supplier, index) => (
                                        <TableRow
                                            key={supplier.id || index}
                                            hover
                                            sx={{
                                                '&:nth-of-type(odd)': {
                                                    backgroundColor: theme.palette.action.hover
                                                }
                                            }}
                                        >
                                            <TableCell>
                                                <Chip
                                                    label={page * rowsPerPage + index + 1}
                                                    color="primary"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{formatCreatedAtDate(supplier.created_at)}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Business color="action" sx={{ mr: 1, fontSize: 16 }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                        {supplier.name}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={<Phone />}
                                                    label={supplier.phone}
                                                    color="primary"
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={<Email />}
                                                    label={supplier.email}
                                                    color="success"
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={<LocationOn />}
                                                    label={supplier.location}
                                                    color="warning"
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Actions">
                                                    <IconButton 
                                                        sx={{ color: theme.palette.action.main }}
                                                        onClick={(e) => handleActionMenuOpen(e, supplier)}
                                                        size="small"
                                                    >
                                                        <MoreVert />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50, 100]}
                            component="div"
                            count={filteredData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{ mt: 2 }}
                        />
                    </Paper>
                )}

                {/* Grid View */}
                {viewMode === 'grid' && renderGridView()}

                {filteredData.length === 0 && viewMode === 'list' && (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        py: 8,
                        color: 'text.secondary'
                    }}>
                        <Business sx={{ fontSize: 60, mb: 2 }} />
                        <Typography variant="h6">No suppliers found</Typography>
                        <Typography variant="body2">Try adjusting your search query</Typography>
                    </Box>
                )}
            </motion.div>

            {/* Filter Options Dialog */}
            <Dialog
                open={filterOptions}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseOptions();
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={clearFilters}
                            sx={{ textTransform: 'none' }}
                        >
                            Show All Suppliers
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<TodayIcon />}
                            onClick={applyTodayFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Show Today's Suppliers
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<CalendarMonth />}
                            onClick={applyThisMonthFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Show This Month's Suppliers
                        </Button>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Fields
                        </Typography>

                        <TextField
                            fullWidth
                            label="Name"
                            name="name"
                            value={filters.name}
                            onChange={handleFilterChange}
                        />

                        <TextField
                            fullWidth
                            label="Phone"
                            name="phone"
                            value={filters.phone}
                            onChange={handleFilterChange}
                        />

                        <TextField
                            fullWidth
                            label="Email"
                            name="email"
                            value={filters.email}
                            onChange={handleFilterChange}
                        />

                        <TextField
                            fullWidth
                            label="Location"
                            name="location"
                            value={filters.location}
                            onChange={handleFilterChange}
                        />

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Date Range
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="From Date"
                                    type="date"
                                    name="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="To Date"
                                    type="date"
                                    name="dateTo"
                                    value={filters.dateTo}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseOptions} variant="contained">
                        Close
                    </Button>
                    <Button onClick={applyFilters} color="primary" variant="contained">
                        Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Supplier Dialog */}
            <Dialog
                open={openAdd}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseAdd();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                scroll="paper"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>{postStatus === "ADD" ? "Add New Supplier" : "Edit Supplier"}</b>
                </DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                        <Box>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}>
                                    Supplier Information
                                </Typography>
                            </Grid>
                            <br />
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Supplier Name *"
                                    name="name"
                                    value={formData.name}
                                    autoComplete="off"
                                    onChange={handleInputChange}
                                    required
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Business />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <br />
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Phone Number *"
                                    name="phone"
                                    value={formData.phone}
                                    autoComplete="off"
                                    onChange={handleInputChange}
                                    error={errors.phone}
                                    helperText={errors.phone ? "Valid phone characters only" : ""}
                                    required
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Phone />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <br />
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Email Address *"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    autoComplete="off"
                                    onChange={handleInputChange}
                                    required
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Email />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <br />
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Location *"
                                    name="location"
                                    value={formData.location}
                                    autoComplete="off"
                                    onChange={handleInputChange}
                                    required
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LocationOn />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                        </Box>

                        <DialogActions sx={{ mt: 3, px: 0 }}>
                            <Button color="error" onClick={handleCloseAdd} variant="contained">
                                Cancel
                            </Button>
                            <Button type="submit" color="primary" variant="contained">
                                {postStatus === "ADD" ? "Add Supplier" : "Update Supplier"}
                            </Button>
                        </DialogActions>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* View Dialog */}
            <Dialog
                open={openView}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseView();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>Supplier Details</b>
                </DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <Box>
                            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                                <Tab label="Details" icon={<Business />} iconPosition="start" />
                                <Tab label="Contact" icon={<Phone />} iconPosition="start" />
                            </Tabs>

                            {activeTab === 0 && (
                                <Box sx={{ p: 2 }}>
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                            <Avatar sx={{ 
                                                bgcolor: theme.palette.primary.main,
                                                width: 64,
                                                height: 64
                                            }}>
                                                <Business sx={{ fontSize: 36 }} />
                                            </Avatar>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="h6" align="center" color="primary">
                                            {selectedRecord.name}
                                        </Typography>
                                    </Grid>
                                    <br />
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2">
                                            <b>Supplier ID:</b> {selectedRecord.id || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <br />
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2">
                                            <b>Location:</b>{' '}
                                            <Chip
                                                icon={<LocationOn />}
                                                label={selectedRecord.location}
                                                color="warning"
                                                variant="outlined"
                                                size="small"
                                                sx={{ ml: 1 }}
                                            />
                                        </Typography>
                                    </Grid>
                                </Box>
                            )}

                            {activeTab === 1 && (
                                <Box sx={{ p: 2 }}>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2">
                                            <b>Phone:</b>{' '}
                                            <Chip
                                                icon={<Phone />}
                                                label={selectedRecord.phone}
                                                color="primary"
                                                variant="outlined"
                                                sx={{ ml: 1 }}
                                            />
                                        </Typography>
                                    </Grid>
                                    <br />
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2">
                                            <b>Email:</b>{' '}
                                            <Chip
                                                icon={<Email />}
                                                label={selectedRecord.email}
                                                color="success"
                                                variant="outlined"
                                                sx={{ ml: 1 }}
                                            />
                                        </Typography>
                                    </Grid>
                                </Box>
                            )}

                            {/* System Information - Card Style */}
                                <SystemInformation
                                    createdBy={selectedRecord.created_by}
                                    updatedBy={selectedRecord.updated_by}
                                    createdAt={selectedRecord.created_at}
                                    updatedAt={selectedRecord.updated_at}
                                    title="Supplier System Information"
                                />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseView} variant="contained">
                        Close
                    </Button>
                    {hasPermission('PRINT') && selectedRecord && (
                        <Button onClick={() => handlePrint(selectedRecord)} color="info" variant="contained">
                            <Print sx={{ mr: 1 }} /> Print
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openDelete}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseDelete();
                    }
                }}
                TransitionComponent={Transition}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Delete Supplier</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
                        Are you sure you want to delete this supplier?
                    </Typography>
                    {selectedRecord && (
                        <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                            {selectedRecord.name}
                            <Box sx={{ mt: 1 }}>
                                <Chip
                                    icon={<Email />}
                                    label={selectedRecord.email}
                                    color="error"
                                    variant="outlined"
                                    size="small"
                                    sx={{ mr: 1 }}
                                />
                                <Chip
                                    icon={<LocationOn />}
                                    label={selectedRecord.location}
                                    color="warning"
                                    variant="outlined"
                                    size="small"
                                />
                            </Box>
                        </Typography>
                    )}
                    <Typography variant="body2" color="error" sx={{ textAlign: 'center', mt: 2 }}>
                        Warning: This action cannot be undone!
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseDelete} variant="contained">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} color="primary" variant="contained">
                        Confirm Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Supplier;